import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Save, Plus, X, Upload, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../components/proposals/SignaturePad';
import CpfCnpjInput from '../components/CpfCnpjInput';
import { validarCNPJ } from '../utils/validators';

const DEFAULT_TERMS = `TERMO DE ACEITE DE ORÇAMENTO
E CONTRATO DE PRESTAÇÃO DE SERVIÇOS


Cláusula 1ª – Objeto do Contrato
O presente instrumento tem por objeto a prestação dos serviços descritos no orçamento identificado acima, de acordo com as especificações, quantidades e condições nele constantes, que passa a fazer parte integrante deste contrato independentemente de transcrição.

Em caso de atraso no pagamento, incidirão multa de 2% sobre o valor em aberto, acrescida de juros de 1% ao mês e correção monetária pelo IGPM/FGV.

Cláusula 3ª – Prazo de Execução
Os serviços serão iniciados a partir da data de aprovação deste orçamento e deverão ser concluídos até o prazo que consta no orçamento, podendo o prazo ser prorrogado mediante acordo entre as partes, por meio de mensagens eletrônicas ou pessoalmente, em casos fortuitos ou de força maior, alterações solicitadas pelo Contratante ou atrasos decorrentes do fornecimento de materiais e/ou informações por parte do Contratante.

Cláusula 4ª – Garantia dos Serviços
O Contratado garante os serviços executados pelo prazo definido no orçamento a contar da data de conclusão e entrega, cobrindo vícios ou defeitos de execução, excluindo-se desgaste natural, mau uso, danos causados por terceiros ou eventos externos alheios ao Contratado.

Cláusula 5ª – Obrigações das Partes
5.1 Obrigações do Contratado:
a) Executar os serviços conforme especificado no orçamento, com qualidade e nos prazos acordados;
b) Manter o Contratante informado sobre o andamento dos serviços;
c) Responsabilizar-se pelos encargos trabalhistas e previdenciários de seus colaboradores.

5.2 Obrigações do Contratante:
a) Efetuar os pagamentos nas condições e nos prazos acordados;
b) Fornecer acesso ao local de trabalho e as informações necessárias para a execução dos serviços;
c) Não contratar terceiros para executar os mesmos serviços durante a vigência deste contrato.

Cláusula 6ª – Rescisão e Cancelamento
O cancelamento do serviço após o início da execução implicará o pagamento proporcional ao trabalho realizado até a data da rescisão, acrescido de multa de 10% (dez por cento) sobre o valor total do contrato, a título de ressarcimento por gastos e reserva de agenda.

Cláusula 7ª – Disposições Gerais
Este instrumento é celebrado com base no artigo 425 do Código Civil Brasileiro (Lei nº 10.406/2002), que autoriza as partes a estipular contratos atípicos. Para fins de validade, a assinatura física ou eletrônica (nos termos da Lei nº 14.063/2020 e MP 2.200-2/2001) tem plena eficácia jurídica. O foro eleito para dirimir eventuais conflitos é o da Comarca de [Cidade – UF], com renúncia a qualquer outro, por mais privilegiado que seja.


Declaro que li, entendi e concordo com todos os termos e condições acima.`;

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [settings, setSettings] = useState({ empresa: {}, contato: {}, categorias: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState('');
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showSignaturePad, setShowSignaturePad] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/settings/');
      setSettings(res.data);
    } catch { toast.error('Erro ao carregar configurações'); }
    finally { setLoading(false); }
  };

  const setEmp = (k) => (e) => setSettings(p => ({ ...p, empresa: { ...p.empresa, [k]: e.target.value } }));
  const setCont = (k) => (e) => setSettings(p => ({ ...p, contato: { ...p.contato, [k]: e.target.value } }));

  const save = async () => {
    // Valida CNPJ se preenchido
    const cnpj = (settings.empresa?.cnpj || '').replace(/\D/g, '');
    if (cnpj && cnpj.length > 0 && !validarCNPJ(cnpj)) {
      toast.error('CNPJ inválido! Verifique os dígitos informados.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/settings/', settings);
      toast.success('Configurações salvas!');
    } catch { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const addCategory = () => {
    if (!newCat.trim()) return;
    if (settings.categorias.includes(newCat.trim())) { toast.error('Categoria já existe'); return; }
    setSettings(p => ({ ...p, categorias: [...p.categorias, newCat.trim()] }));
    setNewCat('');
  };

  const removeCategory = (cat) => {
    setSettings(p => ({ ...p, categorias: p.categorias.filter(c => c !== cat) }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSettings(p => ({ ...p, empresa: { ...p.empresa, logo: ev.target.result } }));
    reader.readAsDataURL(file);
  };

  const changePassword = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Senhas não coincidem'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Senha alterada!');
      setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;

  const e = settings.empresa || {};
  const c = settings.contato || {};

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in pb-10">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-white">Configurações</h1>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
          <Save size={16} />
          {saving ? 'Salvando...' : 'Salvar tudo'}
        </button>
      </div>

      {/* Company */}
      <Section title="Dados da empresa">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="field-label">Logo da empresa</label>
            <div className="flex items-center gap-3">
              {e.logo && <img src={e.logo} alt="Logo" className="h-12 w-auto rounded-lg border border-zinc-700 bg-zinc-800 p-1" />}
              <label className="flex items-center gap-2 border border-zinc-700 hover:border-orange-500/50 text-zinc-300 hover:text-orange-400 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm">
                <Upload size={14} />
                {e.logo ? 'Trocar logo' : 'Carregar logo'}
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
              </label>
              {e.logo && (
                <button onClick={() => setSettings(p => ({ ...p, empresa: { ...p.empresa, logo: null } }))}
                  className="text-zinc-500 hover:text-red-400 transition-colors"><X size={16} /></button>
              )}
            </div>
          </div>
          <div className="col-span-2">
            <label className="field-label">Nome da empresa</label>
            <input className="field-input" value={e.nome || ''} onChange={e => setSettings(p => ({ ...p, empresa: { ...p.empresa, nome: e.target.value.toUpperCase() } }))} />
          </div>
          <div>
            <label className="field-label">CNPJ</label>
            <CpfCnpjInput
              value={e.cnpj || ''}
              onChange={(v) => setSettings(p => ({ ...p, empresa: { ...p.empresa, cnpj: v } }))}
              onlyMode="cnpj"
            />
          </div>
          <div>
            <label className="field-label">Telefone</label>
            <input type="tel" inputMode="tel" className="field-input" value={e.tel || ''} onChange={setEmp('tel')} />
          </div>
          <div>
            <label className="field-label">E-mail</label>
            <input type="email" className="field-input" value={e.email || ''} onChange={setEmp('email')} />
          </div>
          <div>
            <label className="field-label">Site</label>
            <input className="field-input" value={e.site || ''} onChange={setEmp('site')} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Chave PIX</label>
            <input data-testid="chave-pix-input" className="field-input" placeholder="CPF, CNPJ, e-mail, celular ou chave aleatória" value={e.chave_pix || ''} onChange={setEmp('chave_pix')} />
          </div>
          <div>
            <label className="field-label">CEP</label>
            <input className="field-input" value={e.cep || ''} onChange={setEmp('cep')} />
          </div>
          <div>
            <label className="field-label">Número</label>
            <input className="field-input" value={e.num || ''} onChange={setEmp('num')} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Endereço</label>
            <input className="field-input" value={e.end || ''} onChange={e => setSettings(p => ({ ...p, empresa: { ...p.empresa, end: e.target.value.toUpperCase() } }))} />
          </div>
          <div>
            <label className="field-label">Cidade</label>
            <input className="field-input" value={e.cidade || ''} onChange={e => setSettings(p => ({ ...p, empresa: { ...p.empresa, cidade: e.target.value.toUpperCase() } }))} />
          </div>
          <div>
            <label className="field-label">UF</label>
            <input className="field-input" maxLength={2} value={e.uf || ''} onChange={e => setSettings(p => ({ ...p, empresa: { ...p.empresa, uf: e.target.value.toUpperCase() } }))} />
          </div>

          {/* Company Signature */}
          <div className="col-span-2">
            <label className="field-label">Assinatura do responsável</label>
            <div className="flex items-start gap-3 flex-wrap">
              {e.assinatura_empresa ? (
                <div className="flex-1 min-w-0 space-y-2">
                  <img src={e.assinatura_empresa} alt="Assinatura" className="max-h-20 bg-white rounded-lg border border-zinc-600 px-3 py-1" />
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid="redo-signature-btn"
                      onClick={() => setShowSignaturePad(true)}
                      className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-400 hover:text-orange-400 hover:border-orange-500/30 px-3 py-1.5 rounded-lg transition-colors">
                      <PenLine size={12} />
                      Refazer assinatura
                    </button>
                    <button
                      onClick={() => setSettings(p => ({ ...p, empresa: { ...p.empresa, assinatura_empresa: null } }))}
                      className="flex items-center gap-1.5 text-xs border border-zinc-700 text-zinc-500 hover:text-red-400 px-2 py-1.5 rounded-lg transition-colors">
                      <X size={12} />
                      Remover
                    </button>
                  </div>
                  <p className="text-xs text-zinc-600">Esta assinatura será usada automaticamente em todos os orçamentos.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <button
                    data-testid="add-signature-btn"
                    onClick={() => setShowSignaturePad(true)}
                    className="flex items-center gap-2 border border-dashed border-zinc-700 hover:border-orange-500/40 text-zinc-500 hover:text-orange-400 px-4 py-3 rounded-xl transition-colors text-sm">
                    <PenLine size={16} />
                    Adicionar assinatura do responsável
                  </button>
                  <p className="text-xs text-zinc-600">A assinatura será exibida automaticamente em todos os orçamentos.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </Section>

      {/* Garantia */}
      <Section title="Garantia dos Serviços">
        <div className="space-y-4">
          <div>
            <label className="field-label">Prazo de garantia</label>
            <input data-testid="garantia-prazo-input" className="field-input" placeholder="Ex: 90 dias, 6 meses, 1 ano" value={e.garantia_prazo || ''} onChange={setEmp('garantia_prazo')} />
          </div>
          <div>
            <label className="field-label">Condições de garantia</label>
            <textarea data-testid="garantia-condicoes-input" className="field-input min-h-[80px] resize-y" rows={3}
              placeholder="Ex: Garantia cobre defeitos de instalação. Não inclui danos por mau uso..."
              value={e.garantia_condicoes || ''} onChange={setEmp('garantia_condicoes')} />
          </div>
        </div>
      </Section>

      {/* Termos e Condições */}
      <Section title="Termos e Condições">
        <div className="space-y-3">
          <p className="text-xs text-zinc-500">Este texto será exibido no orçamento enviado ao cliente. O campo <span className="text-orange-400 font-mono">[Cidade – UF]</span> será substituído automaticamente pelos dados da sua empresa.</p>
          <textarea
            data-testid="terms-conditions-input"
            className="field-input min-h-[300px] resize-y font-mono text-xs leading-relaxed"
            rows={18}
            placeholder="Cole ou edite o texto dos seus Termos e Condições aqui..."
            value={e.terms_conditions || ''}
            onChange={setEmp('terms_conditions')}
          />
          {!e.terms_conditions && (
            <button
              data-testid="load-default-terms-btn"
              onClick={() => setSettings(p => ({ ...p, empresa: { ...p.empresa, terms_conditions: DEFAULT_TERMS } }))}
              className="text-xs text-orange-400 hover:text-orange-300 border border-orange-500/30 hover:bg-orange-500/10 px-3 py-1.5 rounded-lg transition-colors"
            >
              Carregar modelo padrão
            </button>
          )}
        </div>
      </Section>

      {/* Categories */}
      <Section title="Categorias">
        <div className="flex flex-wrap gap-2 mb-3">
          {settings.categorias.map(cat => (
            <div key={cat} className="flex items-center gap-1.5 bg-zinc-800 border border-zinc-700 rounded-full px-3 py-1.5 text-sm text-zinc-300">
              {cat}
              <button onClick={() => removeCategory(cat)} className="text-zinc-500 hover:text-red-400 transition-colors ml-1">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input className="field-input flex-1" placeholder="Nova categoria..." value={newCat}
            onChange={e => setNewCat(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCategory()} />
          <button onClick={addCategory}
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 px-3 py-2 rounded-xl transition-colors">
            <Plus size={16} />
          </button>
        </div>
      </Section>

      {/* Password */}
      <Section title="Alterar senha">
        <div className="space-y-3">
          <div>
            <label className="field-label">Senha atual</label>
            <input type="password" className="field-input" value={pwForm.current_password}
              onChange={e => setPwForm(p => ({ ...p, current_password: e.target.value }))} />
          </div>
          <div>
            <label className="field-label">Nova senha</label>
            <input type="password" className="field-input" value={pwForm.new_password}
              onChange={e => setPwForm(p => ({ ...p, new_password: e.target.value }))} />
          </div>
          <div>
            <label className="field-label">Confirmar nova senha</label>
            <input type="password" className="field-input" value={pwForm.confirm}
              onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} />
          </div>
          <button onClick={changePassword}
            className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 hover:text-white px-4 py-2 rounded-xl text-sm transition-colors">
            Alterar senha
          </button>
        </div>
      </Section>

      {/* Signature modal */}
      {showSignaturePad && (
        <SignaturePad
          onClose={() => setShowSignaturePad(false)}
          onSave={(sig) => {
            setSettings(p => ({ ...p, empresa: { ...p.empresa, assinatura_empresa: sig } }));
            setShowSignaturePad(false);
            toast.success('Assinatura salva! Clique em "Salvar tudo" para confirmar.');
          }}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-zinc-800">
        <h2 className="font-heading font-semibold text-white">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}
